import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListRecipesResponse, Perimetre, RecipeByIngredients, RecipeListItem } from '@mlc/shared';
import { listRecipes, searchByIngredients, setRecipesVisibility } from './api';
import { LibraryFilters } from './LibraryFilters';
import { useSasEnAttente } from './sasEnAttente';
import { useSession } from './session';
import { PantrySearch } from './PantrySearch';
import { APPAREIL_DU_FONDS, NOM_DU_FONDS, canEdit, estDuFonds, formatRelativeDate, formatServings, formatTotalDuration } from '@mlc/shared';
import { Link, navigate, paths, useSearchParam, useUrl, withSearchParam, withSearchParams } from './router';
import { Badge, Banner, Button, EmptyState, FOCUS_RING, INTERACTIVE, Icon, LogoMark, PageHeader, RecipeThumb, SourceMark, buttonClasses, cardClasses } from './ui';

/**
 * Style visuellement distinct des tags automatiques (US-05 : « ne sont pas
 * modifiables par l'utilisateur ») — tokens du design system (index.css),
 * mêmes couleurs sémantiques que le badge de filtre ci-dessous. Couleurs
 * exportées par `ui/Badge.tsx` (B1) : les puces de filtre ci-dessous
 * réutilisent directement `TAG_TONE_CLASSES`, les badges statiques des
 * cartes passent par le composant `Badge`.
 */
function tagTone(kind: RecipeListItem['tags'][number]['kind']): 'auto' | 'manual' {
  return kind === 'auto' ? 'auto' : 'manual';
}

export interface RecipeListProps {
  /** Incrémenté pour forcer un rechargement (ex. après création d'une recette). */
  refreshKey: number;
  /**
   * Liste des recettes archivées plutôt que de la bibliothèque. Décidé par la
   * route depuis US-13 (`/archives`) et non plus par un état local : les
   * archives sont un écran à part entière, donc une adresse, atteignable par
   * lien et restituée par le bouton retour.
   */
  archived: boolean;
  /**
   * Périmètre demandé au serveur (US-97). Décidé par la route, comme
   * `archived` : `livre` sur la bibliothèque, `fonds` sur l'écran du fonds
   * Thermomix, `tout` sur les archives — une recette du fonds qu'on a archivée
   * ne se retrouve nulle part ailleurs, et la rendre invisible ici la rendrait
   * irrécupérable.
   *
   * **Il n'est pas appliqué ici**, il est *demandé* : la liste qui arrive est
   * déjà la bonne. Filtrer dans ce composant aurait donné le même écran, un
   * compteur faux, et huit fois trop de données à chaque chargement.
   */
  perimetre: Perimetre;
}

/** Icône neutre (assiette + couverts), utilisée par les états vides — jamais d'emoji, jamais d'illustration générique (§6 mission). */
function PhotoPlaceholder(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 48 48"
      aria-hidden="true"
      className="size-10 text-foreground-muted opacity-60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="24" cy="24" r="13" />
      <circle cx="24" cy="24" r="5.5" />
      <path d="M8 8v11M8 8h2.5M8 8H5.5M8 15h2.5M8 15H5.5M40 8v32M40 8c-2.5 0-4 2-4 5v6c0 2.5 1.5 4 4 4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Signal de mémoire de la carte (US-23) : « ★ 5/5 · il y a 3 semaines ».
 * C'est la seule information qu'aucune autre application de recettes ne peut
 * afficher, et elle occupe donc la dernière ligne de la carte, à place fixe.
 *
 * `null` pour une recette jamais cuisinée : rien ne s'affiche plutôt qu'un
 * « jamais cuisinée » qui alourdirait chaque carte d'une bibliothèque neuve.
 * La place, elle, reste réservée (`min-h-5` côté carte) — sans quoi les
 * cartes d'une même rangée se termineraient à des hauteurs différentes, ce
 * qui est précisément le défaut que cette story corrige.
 */
function MemorySignal({
  latestCookLog,
  now,
}: {
  latestCookLog: RecipeListItem['latest_cook_log'];
  now: number;
}): React.JSX.Element | null {
  // `== null` (pas `=== null`) : filet de sécurité si la réponse serveur ou
  // un test omet le champ (`undefined`) plutôt que de le mettre à `null` —
  // la réponse HTTP n'est jamais validée par le système de types à l'exécution.
  if (latestCookLog == null) return null;
  return (
    <p className="flex items-center gap-1.5 text-xs text-foreground-secondary">
      {latestCookLog.rating !== null && (
        <span className="text-data font-medium text-primary">
          <Icon name="etoile" size={0.9} className="mr-0.5" />
          <span className="sr-only">Noté </span>
          {latestCookLog.rating}/5
        </span>
      )}
      <span>
        {latestCookLog.rating !== null && <span aria-hidden="true">· </span>}
        Cuisinée {formatRelativeDate(latestCookLog.cooked_at, now)}
      </span>
    </p>
  );
}

/**
 * Squelette de chargement calqué sur la carte réelle, forme horizontale
 * comprise : un squelette qui ne se substitue pas exactement à ce qui arrive
 * produit un saut de mise en page au moment de l'échange.
 */
function RecipeCardSkeleton(): React.JSX.Element {
  return (
    <div
      className={cardClasses({
        padded: false,
        surface: true,
        className: 'flex h-full animate-pulse overflow-hidden xs:flex-col',
      })}
    >
      <div className="aspect-square w-28 shrink-0 bg-surface-sunken xs:aspect-[3/2] xs:w-full" />
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="h-4 w-3/4 rounded-control bg-surface-sunken" />
        <div className="h-3 w-1/2 rounded-control bg-surface-sunken" />
      </div>
    </div>
  );
}

/** Deux puces au maximum sur une carte, plus un « +N » : au-delà, la carte cesse d'être lisible d'un coup d'œil (US-23). */
const MAX_CARD_TAGS = 2;

/**
 * Densité de la grille (US-23), en une seule constante partagée par la liste
 * et son squelette de chargement — deux jeux de colonnes différents
 * produiraient un saut de mise en page au moment de l'échange.
 *
 * Une colonne sous 480 px (carte horizontale compacte), deux à 768 px, trois
 * à 1024 px, quatre à 1280 px, cinq au-delà de 1700 px. Rien entre 480 et
 * 768 px : c'est la plage du téléphone en paysage et de la petite tablette,
 * où deux colonnes donneraient des cartes de 220 px, trop étroites pour un
 * titre sur deux lignes.
 *
 * **La gouttière passe de 12/16 px à 16/24 px** (refonte éditoriale). À
 * 16 px, quatre cartes blanches sur un fond crème formaient une nappe
 * continue où l'œil ne séparait plus les plats : c'est le blanc *entre* les
 * cartes qui fait qu'une grille de photos se lit comme une planche
 * d'illustrations plutôt que comme un tableau.
 */
const GRID_BASE_CLASSES = 'grid grid-cols-1 gap-4 xs:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-5';

/**
 * Réserve de fin de liste pour le bouton flottant « Ajouter une recette »
 * (US-90, F-02). Elle doit dégager **la hauteur du bouton**, pas seulement sa
 * position : `size="large"` (`ui/Button.tsx`) donne `min-h-touch` sans
 * padding vertical, soit 48 px (`--spacing-touch`).
 *
 * - Mobile : le bouton est posé à `bottom-24` (96 px du bas) ; son bord haut
 *   est donc à 96 + 48 = 144 px. L'ancienne réserve (`pb-24`, 96 px)
 *   s'arrêtait pile à la position du bouton sans compter sa hauteur — la
 *   dernière carte restait recouverte, défilement terminé, exactement le
 *   défaut F-02. `pb-40` (160 px) dégage les 144 px plus 16 px de
 *   respiration, la même que la gouttière ci-dessus.
 * - Grand écran : le bouton passe à `lg:bottom-8` (32 px), bord haut à
 *   32 + 48 = 80 px. `lg:pb-24` (96 px) dégage ces 80 px plus les mêmes
 *   16 px — et remplace l'ancien `lg:pb-0`, qui retirait la réserve sans que
 *   le bouton, lui, ne s'efface (il reste `lg:bottom-8`) : la pilule
 *   recouvrait la dernière rangée à ≥ 1024 px, où la réserve avait été
 *   explicitement défaite.
 *
 * Appliquée seulement quand le bouton existe réellement (ni archives, ni
 * mode cuisine partagée) : une réserve sans bouton au-dessus serait un vide
 * sans raison, à l'écran comme dans le code — cf. `reserveFAB` ci-dessous.
 * Dans la constante partagée et non sur la seule liste : le squelette de
 * chargement doit réserver exactement la même boîte, sinon l'échange produit
 * un saut — ce que `recipeCard.test.tsx` vérifie en comparant les deux jeux
 * de classes à l'identique.
 */
const FAB_RESERVE_CLASSES = 'pb-40 lg:pb-24';

/**
 * Carte de recette (US-23). Trois défauts corrigés d'un coup :
 *
 * 1. **Hauteur uniforme.** Les cartes d'une même rangée se terminaient de 331
 *    à 389 px selon le nombre de puces et la présence d'une ligne de journal,
 *    donnant un bas de grille en dents de scie. Ici, la carte occupe toute la
 *    hauteur de sa cellule (`h-full`), les puces sont plafonnées à deux, et le
 *    signal de mémoire est poussé en bas (`mt-auto`) à hauteur réservée.
 * 2. **Plus jamais de rectangle vide.** À défaut de photo, `RecipeThumb`
 *    peint un dégradé déterministe et l'initiale du titre.
 * 3. **Contenu recentré sur la décision.** Titre, temps total formaté (US-16),
 *    nombre d'ingrédients, et la mémoire quand elle existe. La difficulté
 *    quitte la carte : trois pastilles de 6 px sans légende ne se lisaient
 *    pas — elle est en toutes lettres sur la fiche (US-14).
 */
function RecipeCard({
  recipe,
  now,
  missing,
  couverture = null,
  selectionnee = null,
  onSelection,
  modifiable = true,
  supprimable = false,
}: {
  recipe: RecipeListItem;
  now: number;
  /** Ingrédients manquants (US-29), ou `null` hors du mode « avec ce que j'ai ». */
  missing?: string[] | null;
  /**
   * Combien des ingrédients demandés cette recette utilise, sur combien
   * (US-101). `null` hors du mode « avec ce que j'ai », ou quand un seul
   * ingrédient est demandé — « utilise 1 de vos 1 » n'apprend rien.
   */
  couverture?: { utilises: number; demandes: number } | null;
  /** `null` hors du mode tri en lot (US-49) ; sinon, l'état de la case. */
  selectionnee?: boolean | null;
  onSelection?: (cochee: boolean) => void;
  /** La recette est-elle modifiable par le demandeur (`canEdit`, shared/src/access.ts) ? */
  modifiable?: boolean;
  /**
   * La suppression définitive est-elle atteignable depuis cette carte (US-83) ?
   * Faux partout sauf sur l'écran des recettes archivées, pour le
   * `proprietaire`, sur ses propres recettes.
   */
  supprimable?: boolean;
}): React.JSX.Element {
  /** La photo a-t-elle échoué à charger ? Voir le repli sur `RecipeThumb` ci-dessous. */
  const [photoEnEchec, setPhotoEnEchec] = useState(false);

  // Temps total plutôt que préparation et cuisson côte à côte (US-16) :
  // c'est le premier critère de choix, et il n'était affiché nulle part.
  const total = formatTotalDuration(recipe);
  // « De qui » est une information de premier plan dans un livre familial
  // (US-49) : sans elle, une recette partagée par quelqu'un d'autre est
  // indiscernable des siennes, et on s'étonne de ne pas pouvoir la modifier.
  // Affichée seulement quand ce n'est pas la sienne — « de Arthur » sur chacune
  // de ses propres recettes serait du bruit. En tête de la ligne de méta, parce
  // que c'est ce qui change le plus le regard qu'on porte sur la carte.
  const auteur = modifiable || recipe.owner_name === null ? null : `de ${recipe.owner_name}`;

  const meta = [
    auteur,
    total !== null ? total.label : null,
    recipe.ingredient_count > 0
      ? `${recipe.ingredient_count} ingrédient${recipe.ingredient_count > 1 ? 's' : ''}`
      : null,
    formatServings(recipe.servings, recipe.servings_label),
  ].filter((part): part is string => part !== null);

  const visibleTags = recipe.tags.slice(0, MAX_CARD_TAGS);
  const hiddenTagCount = recipe.tags.length - visibleTags.length;
  /** Ce que le « +N » cache, en toutes lettres — « Aussi : sans four, été ». */
  const etiquettesCachees = hiddenTagCount === 0
    ? undefined
    : `Aussi : ${recipe.tags.slice(MAX_CARD_TAGS).map((tag) => tag.name).join(', ')}`;

  // En mode tri, la carte cesse d'être un lien : cliquer dessus coche, et
  // ouvrir la fiche n'est pas ce qu'on cherche à faire à ce moment-là. Un lien
  // qui, selon un mode, ouvre ou coche serait exactement le genre de contrôle
  // ambigu que le §4 proscrit.
  if (selectionnee !== null) {
    // **La recette d'un autre ne se coche pas.** Le serveur l'ignore déjà en
    // silence, ce qui est juste pour une recette dont la visibilité a changé
    // entre l'affichage et l'action — mais pas ici : l'interface sait depuis le
    // premier rendu que celle-ci ne lui appartient pas. Proposer un geste sans
    // effet, c'est promettre puis ne rien faire, et c'est pire que ne rien
    // proposer.
    if (!modifiable) {
      return (
        <div
          data-testid={`recipe-verrouillee-${recipe.id}`}
          className={cardClasses({
            padded: false,
            surface: true,
            className: 'flex h-full w-full items-center gap-3 overflow-hidden p-3 opacity-60',
          })}
        >
          <span aria-hidden="true" className="h-11 w-11 shrink-0" />
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate font-medium">{recipe.title}</span>
            <span className="text-sm text-foreground-secondary">
              {recipe.owner_name === null
                ? 'Vous ne pouvez pas la modifier'
                : `Recette de ${recipe.owner_name}`}
            </span>
          </span>
        </div>
      );
    }

    return (
      <button
        type="button"
        data-testid={`recipe-select-${recipe.id}`}
        aria-pressed={selectionnee}
        onClick={() => { onSelection?.(!selectionnee); }}
        className={cardClasses({
          padded: false,
          surface: true,
          className: `flex h-full w-full items-center gap-3 overflow-hidden p-3 text-left transition-colors ${
            selectionnee ? 'border-primary bg-primary-soft' : ''
          }`,
        })}
      >
        {/* La coche était le caractère Unicode `✓`, ce que le §4 de
            CLAUDE.md proscrit explicitement — son dessin dépend de la police
            installée, il ne se dimensionne pas et n'hérite d'aucune couleur
            commune. `ui/Checkbox.tsx` dessine la sienne en SVG depuis US-25
            « parce qu'une police peut ne pas l'avoir » ; celle-ci était
            restée en arrière. */}
        <span
          aria-hidden="true"
          className={`flex size-touch-min shrink-0 items-center justify-center rounded-control border-2 transition-colors ${
            selectionnee ? 'border-primary bg-primary text-primary-foreground' : 'border-border-interactive'
          }`}
        >
          {selectionnee && <Icon name="coche" size={1.2} />}
        </span>
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate font-medium">{recipe.title}</span>
          <span className="text-sm text-foreground-secondary">
            {recipe.visibility === 'shared' ? 'Dans le livre de famille' : 'Gardée pour moi'}
          </span>
        </span>
      </button>
    );
  }

  return (
    // Vrai lien depuis US-13, plus un `<button>` : ouverture dans un nouvel
    // onglet, clic milieu, copie de l'adresse, aperçu de la cible au survol —
    // et le curseur pointeur que le `<button>` n'avait pas (constat 18).
    //
    // Le raccourci de cuisson est **à côté** du lien de carte, pas dedans :
    // un lien dans un lien n'est pas du HTML valide, et le navigateur en fait
    // ce qu'il veut. Il est superposé par positionnement (US-58).
    // `w-full` manquait, et c'est **la** cause du bas de grille en dents de
    // scie que la carte d'US-23 croyait avoir corrigé.
    //
    // Ce conteneur est né avec le raccourci de cuisson (US-58), qui doit être
    // superposé à la photo et non imbriqué dans le lien de carte — un lien
    // dans un lien n'est pas du HTML valide. Il a reçu `relative h-full`,
    // pas `w-full`. Or c'est lui, désormais, qui est l'élément de la grille :
    // sans largeur imposée, un élément flex se dimensionne sur son contenu,
    // et le `w-full` du lien à l'intérieur ne référence plus que cette
    // largeur-là. Mesuré sur la bibliothèque réelle : quatre colonnes de
    // 298 px portant des cartes de 298, 227, 235 et 229 px.
    //
    // Invisible en relecture (chaque classe est correcte prise isolément),
    // invisible aux tests (jsdom ne calcule aucune mise en page), et lisible
    // seulement sur une capture — d'où deux ans de « ça fait fouillis » sans
    // cause nommable.
    <div className="relative h-full w-full">
    <Link
      to={paths.recipe(recipe.id)}
      data-testid={`recipe-card-${recipe.id}`}
      // Nom accessible réduit au titre. Sans lui, le lien s'annonce
      // « Ratatouille 45 min · 6 ingrédients · 4 personnes ★ 4/5 Cuisinée il y
      // a 3 semaines » — la liste des liens, principal moyen de parcourir une
      // page au lecteur d'écran, devient illisible. Le reste de la carte
      // demeure lu en mode navigation : il est déplacé, pas supprimé.
      aria-label={recipe.archived === 1 ? `${recipe.title} (archivée)` : recipe.title}
      className={cardClasses({
        padded: false,
        surface: true,
        elevation: 'hover',
        // Survol composé (US-23) : élévation, bordure en accent, zoom d'image
        // à 1,03 — trois signaux légers valent mieux qu'un seul appuyé, et le
        // même geste reste perceptible en thème clair comme en sombre.
        // Recette archivée : légèrement estompée, cet écran n'étant consulté
        // que pour restaurer ou vérifier, jamais pour cuisiner dans l'instant.
        className: `group flex h-full w-full overflow-hidden text-left no-underline transition-colors hover:border-primary xs:flex-col ${recipe.archived === 1 ? 'opacity-70' : ''}`,
      })}
    >
      {/* Forme horizontale compacte sous 480 px (téléphone tenu en main) : la
          photo devient une vignette carrée de 112 px à gauche, et six cartes
          tiennent dans un écran au lieu de deux. Au-dessus, ratio 4:3. */}
      {/* `bg-surface-sunken` et non `bg-surface-raised` : l'espace réservé à
          une photo est un **creux**, pas un relief. Sur le thème clair, un
          rectangle blanc sur une carte blanche ne se voyait pas du tout le
          temps du chargement — la carte semblait amputée.

          Ratio 3:2 et non 4:3 au-dessus de 480 px : c'est le format d'un
          appareil photo, donc celui de la quasi-totalité des images que
          l'application reçoit. En 4:3, chaque photo importée était recadrée
          d'un huitième de sa hauteur, ce qui coupait systématiquement le haut
          des assiettes prises de trois quarts. */}
      <div className="aspect-square w-28 shrink-0 overflow-hidden bg-surface-sunken xs:aspect-[3/2] xs:w-full">
        {recipe.image_path !== null && !photoEnEchec ? (
          <img
            src={`/api/photos/${recipe.image_path}`}
            alt=""
            loading="lazy"
            // Une photo qui ne charge pas laissait un rectangle vide, sans
            // rien pour le distinguer d'une carte inachevée — le défaut même
            // que `RecipeThumb` a été écrite pour supprimer (US-23), mais
            // qu'elle ne couvrait que dans le cas `image_path === null`. Le
            // fichier peut manquer, la route peut répondre 404 après une
            // restauration partielle : le repli est le même dessin.
            onError={() => { setPhotoEnEchec(true); }}
            className="h-full w-full object-cover transition-transform duration-(--duration-settle) ease-entrance group-hover:scale-[1.04]"
          />
        ) : (
          <div className="h-full w-full transition-transform duration-(--duration-settle) ease-entrance group-hover:scale-[1.04]">
            <RecipeThumb recipeId={recipe.id} title={recipe.title} />
          </div>
        )}
      </div>

      {/* `pr-14` sous 480 px : **la place du raccourci de cuisson**, qui est
          superposé et qui, sur la carte horizontale du téléphone, tombait sur
          le titre. Réserver plutôt que déplacer — le bouton reste au même
          endroit pour la main, et le texte ne passe plus dessous. Le défaut
          venait d'US-58, écrite pour la carte verticale où ce coin est celui
          de la photo ; la carte horizontale de moins de 480 px n'y a jamais
          été relue. */}
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-3 pr-14 xs:p-4 xs:pr-4">
        {/* Le titre passe de `text-base` (16 px) à `text-lg` (18 px) et gagne
            l'axe optique de Fraunces : c'est le nom du plat, la seule chose
            qu'on lit vraiment en parcourant une grille. */}
        <p className="flex items-start gap-2 font-display text-lg leading-tight font-semibold tracking-[-0.012em] text-foreground">
          {/* Deux lignes au maximum, troncature au-delà : un titre long ne
              doit pas pouvoir déformer sa carte. */}
          <span className="line-clamp-2">{recipe.title}</span>
          {recipe.archived === 1 && <Badge tone="neutral">Archivée</Badge>}
          {/* **Ce qu'il faut avoir pour la faire** (US-97). « 20 sec /
              vitesse 5 » ne se lit que devant un Thermomix, et l'apprendre en
              ouvrant la fiche est une déception de trop.

              En toutes lettres, jamais un glyphe : un pictogramme monochrome
              dit « ça vient d'ailleurs », il ne dit pas « il te faut une
              machine ». Et **près du titre, pas dans la rangée d'étiquettes**
              — une étiquette est calculée ou personnelle (§1 « Rangement »),
              celle-ci n'est ni l'une ni l'autre : c'est une propriété du
              fonds, portée par `source_name`. */}
          {estDuFonds(recipe) && <Badge tone="neutral">{APPAREIL_DU_FONDS}</Badge>}
        </p>

        {/* `text-meta` : le rang « service » du design system. En `text-sm
            text-foreground-secondary`, la ligne de durées pesait presque
            autant que le titre juste au-dessus. */}
        {meta.length > 0 && <p className="text-meta">{meta.join(' · ')}</p>}

        {visibleTags.length > 0 && (
          <p className="flex flex-wrap gap-1">
            {visibleTags.map((tag) => (
              <Badge key={tag.id} tone={tagTone(tag.kind)}>
                {tag.name}
              </Badge>
            ))}
            {/* **Le « +N » nomme ce qu'il cache.** Il ne le disait pas : une
                pastille qui annonce un reste sans jamais le montrer demande
                d'ouvrir la fiche pour savoir de quoi elle parle, ce qui est
                exactement le contraire d'une carte lisible d'un coup d'œil
                (US-23). Le plafond de deux puces, lui, ne bouge pas — c'est lui
                qui garde une grille de vingt cartes tenable, et un test le
                mesure depuis US-56.

                `title` pour la souris, `aria-label` pour une synthèse vocale.
                Au doigt, ni l'un ni l'autre ne s'ouvre : la fiche reste le
                chemin complet, à une tape. */}
            {hiddenTagCount > 0 && (
              <Badge tone="neutral" title={etiquettesCachees} aria-label={etiquettesCachees}>
                +{hiddenTagCount}
              </Badge>
            )}
          </p>
        )}

        {/* Manque à combler (US-29). Dit en toutes lettres : un classement par
            manquants sans explication n'est qu'un ordre inexpliqué.

            **Et la couverture aussi** (US-101). Depuis que le classement se
            fait d'abord sur « combien de ce que j'ai la recette utilise », la
            carte doit le dire, sinon l'ordre redevient inexpliqué : deux
            recettes qui manquent d'autant de choses ne se rangent plus au
            hasard, et l'écran doit montrer pourquoi. */}
        {missing !== undefined && missing !== null && (
          <p className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs ${missing.length === 0 ? 'text-success' : 'text-foreground-secondary'}`}>
            {/* `✓` en toutes lettres : même défaut que la case du tri en lot,
                et §4 le proscrit pour la même raison. */}
            {missing.length === 0 ? (
              <>
                <Icon name="coche" size={1} />
                Vous avez tout
              </>
            ) : (
              <>
                {couverture !== null && (
                  <span className="font-medium text-foreground">
                    Utilise {couverture.utilises} de vos {couverture.demandes} ingrédients ·
                  </span>
                )}
                <span>
                  {`Il vous manque : ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? `, +${missing.length - 3}` : ''}`}
                </span>
              </>
            )}
          </p>
        )}

        {/* `mt-auto` + hauteur réservée : la dernière ligne est à la même
            hauteur sur toutes les cartes de la rangée, qu'elle porte ou non
            un signal de mémoire. C'est ce qui rend le bas de grille droit. */}
        <div className="mt-auto min-h-5 pt-1">
          <MemorySignal latestCookLog={recipe.latest_cook_log} now={now} />
        </div>
      </div>
    </Link>

    {/* **D'où vient cette recette, et le chemin pour y retourner** (US-74).
        Absente de la quasi-totalité des cartes — une recette saisie à la main
        n'a pas de provenance à montrer — et c'est ce qui la rend lisible quand
        elle est là.

        **À côté du lien de carte, pas dedans**, pour la même raison que le
        raccourci de cuisson juste en dessous : la carte est un lien, et un lien
        dans un lien n'est pas du HTML valide. Angle haut-gauche, celui-là
        occupant le haut-droit — les deux sont sur la photo dans les deux mises
        en page, la verticale comme l'horizontale de moins de 480 px. */}
    {/* `left-0 top-0` et non `left-2 top-2` : la boîte de 44 px est plus
        grande que la pastille de 28 px qu'elle centre, si bien que le dessin
        se retrouve à 8 px du coin — exactement où se pose le raccourci de
        cuisson, dont la boîte de 48 px est, elle, entièrement visible. */}
    <SourceMark recipe={recipe} className="absolute left-0 top-0 z-10" />

    {/* **Un geste entre la bibliothèque et la première étape** (US-58).
        Écarté une première fois — « un contrôle de plus sur un écran qui en
        porte déjà vingt » — et rouvert une fois l'accueil allégé par US-56,
        comme prévu.

        Icône seule, dans le coin de la photo : un libellé sur chacune des
        trente cartes rendrait la grille bavarde, et la cible reste distincte
        de la carte, qu'on ouvre en touchant n'importe où ailleurs. Le nom
        accessible porte le titre — « Cuisiner Ratatouille » — sans quoi une
        liste de liens annoncerait trente fois « Cuisiner ».

        Jamais sur une recette archivée : cet écran se consulte pour restaurer
        ou vérifier, pas pour cuisiner dans l'instant. */}
    {recipe.archived === 0 && (
      <Link
        to={paths.cook(recipe.id)}
        aria-label={`Cuisiner ${recipe.title}`}
        title={`Cuisiner ${recipe.title}`}
        // **Il n'est plus un aplat orange plein.** Répété sur trente cartes,
        // il posait sur la bibliothèque une trame de trente carrés orange qui
        // captait le regard avant les photos — l'écran vitrine se lisait
        // comme un tableau de bord. Verre dépoli sur la photo au repos, plein
        // au survol et au focus : présent, mais après le plat.
        //
        // `opacity-0 group-hover:opacity-100` serait un piège tactile — sur
        // une tablette il n'y a pas de survol, donc le bouton n'existerait
        // jamais. Il reste visible partout ; c'est son poids qui change.
        // Classes écrites ici plutôt que `buttonClasses('primary', …)` : la
        // variante primaire pose `bg-primary`, et une classe de fond ajoutée
        // en fin de chaîne ne la recouvre pas — deux utilitaires de même
        // spécificité se départagent par leur **ordre dans la feuille
        // compilée**, jamais par leur ordre dans l'attribut `class`. C'est
        // exactement le genre de correction qui semble appliquée et ne l'est
        // pas.
        className={`inline-flex size-touch items-center justify-center rounded-control border border-white/30 bg-black/35 text-white shadow-card backdrop-blur-md transition duration-(--duration-quick) ease-entrance active:scale-[0.97] hover:bg-primary hover:text-primary-foreground hover:border-primary focus-visible:bg-primary focus-visible:text-primary-foreground ${FOCUS_RING} absolute right-2 top-1/2 z-10 -translate-y-1/2 xs:top-2 xs:translate-y-0`}
      >
        <Icon name="casserole" size={1.1} />
      </Link>
    )}

    {/* **La suppression définitive, sur la carte** (US-83, D17) — au même
        coin, et ce n'est pas un hasard : le raccourci de cuisson n'existe pas
        sur une recette archivée, et cet écran ne se consulte que pour trier.
        La place est libre parce que le geste qu'on vient y faire a changé.

        **Elle ne supprime pas : elle mène.** Le décompte de ce qui va
        disparaître (D16) et la proposition d'export ne tiennent pas au coin
        d'une vignette, et D17 veut deux gestes séparés dans le temps. La
        commande existe donc bien ici, et ce qu'elle ouvre est la fiche, à
        l'ancre du panneau de suppression — un pas de plus, qui est
        exactement ce que la décision demande.

        Le nom accessible porte le titre : une grille de trente cartes
        annoncerait sinon trente fois « Supprimer ». */}
    {supprimable && (
      <Link
        to={`${paths.recipe(recipe.id)}#suppression`}
        aria-label={`Supprimer définitivement ${recipe.title}`}
        title={`Supprimer définitivement ${recipe.title}`}
        // Mêmes classes que le raccourci de cuisson, au rouge près : c'est le
        // même objet — une commande posée sur la photo, lisible sur n'importe
        // quelle image — et la seule différence qui compte est ce qu'elle
        // fait. Le rouge n'arrive qu'au survol et au focus : trente pastilles
        // rouges feraient de l'écran des archives un tableau d'alertes.
        className={`inline-flex size-touch items-center justify-center rounded-control border border-white/30 bg-black/35 text-white shadow-card backdrop-blur-md transition duration-(--duration-quick) ease-entrance active:scale-[0.97] hover:bg-danger hover:text-white hover:border-danger focus-visible:bg-danger focus-visible:text-white ${FOCUS_RING} absolute right-2 top-1/2 z-10 -translate-y-1/2 xs:top-2 xs:translate-y-0`}
      >
        <Icon name="corbeille" size={1.1} />
      </Link>
    )}
    </div>
  );
}

/**
 * Barre du tri en lot (US-49, cas 19).
 *
 * Elle n'apparaît qu'à la demande : la bibliothèque reste un écran de lecture,
 * et des cases à cocher permanentes sur chaque carte transformeraient l'écran
 * vitrine en écran de gestion. Le geste est ponctuel — massivement au
 * lancement du livre familial, rarement ensuite.
 */
function BarreDeTri({
  selection, total, verrouillees, enCours, erreur, onFermer, onToutCocher, onAppliquer,
}: {
  selection: Set<string> | null;
  /** Nombre de recettes réellement modifiables — « Tout cocher » ne vise que celles-là. */
  total: number;
  /** Recettes affichées mais appartenant à quelqu'un d'autre. */
  verrouillees: number;
  enCours: boolean;
  erreur: string | null;
  onFermer: () => void;
  onToutCocher: () => void;
  onAppliquer: (visibility: 'private' | 'shared') => void;
}): React.JSX.Element | null {
  const [confirmation, setConfirmation] = useState<'private' | 'shared' | null>(null);

  // Le déclencheur (« Composer le livre de famille ») a rejoint les actions
  // de l'en-tête : c'est un **mode**, comme les archives, et non un contrôle
  // de la liste. Il flottait seul, aligné à droite, entre le compteur de
  // résultats et la première rangée de cartes — la seule place de l'écran où
  // rien ne l'expliquait.
  if (selection === null) return null;

  // `rounded-card` et non `rounded-lg` : §4 CLAUDE.md proscrit `rounded-lg`,
  // qui fait doublon avec `rounded-control`, et une barre de mode n'est pas
  // un contrôle mais un panneau. `border-primary bg-primary-soft` : le mode
  // se signale par une teinte douce plutôt que par `bg-primary/5`, une
  // opacité arbitraire hors de la palette.
  return (
    <div className="flex flex-col gap-3 rounded-card border border-primary bg-primary-soft p-4">
      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}
      {confirmation !== null && (
        <div className="flex flex-col gap-2">
          <Banner tone="info">
            {confirmation === 'shared'
              ? `${String(selection.size)} recette${selection.size > 1 ? 's rejoindront' : ' rejoindra'} le livre de famille. Vos notes, votre journal et votre rangement restent à vous.`
              : `${String(selection.size)} recette${selection.size > 1 ? 's redeviendront invisibles' : ' redeviendra invisible'} pour les autres, et les liens déjà envoyés cesseront de fonctionner.`}
          </Banner>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="primary"
              disabled={enCours}
              onClick={() => { const vers = confirmation; setConfirmation(null); onAppliquer(vers); }}
            >
              Confirmer
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setConfirmation(null); }}>Annuler</Button>
          </div>
        </div>
      )}
      <p className="text-sm">
        {selection.size === 0
          ? 'Cochez les recettes à mettre dans le livre de famille.'
          : `${String(selection.size)} recette${selection.size > 1 ? 's' : ''} sélectionnée${selection.size > 1 ? 's' : ''}.`}
      </p>
      {/* Dire pourquoi certaines ne se cochent pas, plutôt que de laisser
          chercher : une carte grisée sans explication se lit comme un défaut. */}
      {verrouillees > 0 && (
        <p className="text-sm text-foreground-secondary">
          {verrouillees === 1
            ? 'Une recette n’est pas la vôtre : son propriétaire seul décide qui la voit. Ouvrez-la pour en faire une copie.'
            : `${String(verrouillees)} recettes ne sont pas les vôtres : leur propriétaire seul décide qui les voit. Ouvrez-en une pour en faire une copie.`}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {/* Confirmation des deux côtés (US-56). La fiche en exigeait une, la
            bascule en lot l'appliquait immédiatement — alors que c'est le geste
            le plus lourd de conséquences : « les garder pour moi » révoque d'un
            coup les liens déjà envoyés de toutes les recettes cochées. */}
        <Button
          type="button"
          variant="primary"
          disabled={enCours || selection.size === 0}
          onClick={() => { setConfirmation('shared'); }}
        >
          Les mettre dans le livre
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={enCours || selection.size === 0}
          onClick={() => { setConfirmation('private'); }}
        >
          Les garder pour moi
        </Button>
        <Button type="button" variant="ghost" disabled={enCours || total === 0} onClick={onToutCocher}>
          Tout cocher
        </Button>
        <Button type="button" variant="ghost" onClick={onFermer}>Terminer</Button>
      </div>
    </div>
  );
}

/**
 * Bibliothèque (écran d'accueil, §6 brief) : recherche plein texte à la
 * frappe (US-04) et tags — automatiques et manuels, visuellement distincts,
 * filtrables d'un clic (US-05). Le filtre par tag est purement local (les
 * tags de la page déjà chargée), aucune route supplémentaire : les résultats
 * affichés viennent toujours de la même requête `listRecipes`.
 *
 * Présentation en cartes avec photo (refonte UX/UI, passe 4) : la bibliothèque
 * est l'écran vitrine, elle doit donner envie d'ouvrir une recette — une
 * simple liste de titres ne le permettait pas.
 *
 * **Trois écrans, un composant** (US-97) : la bibliothèque, les archives, et
 * le fonds Thermomix. Ce sont la même grille, les mêmes filtres, la même
 * recherche — seuls changent l'en-tête, les destinations voisines et le
 * périmètre demandé au serveur. En écrire un second aurait produit deux cartes
 * qui divergent à la première correction.
 */
export function RecipeList({ refreshKey, archived, perimetre }: RecipeListProps): React.JSX.Element {
  const [recipes, setRecipes] = useState<ListRecipesResponse | null>(null);
  /**
   * Tri en lot (US-49, cas 19). Après la migration de la vague E, tout est
   * privé et il faut décider recette par recette. Sans ce geste, l'étape est
   * pénible, sera repoussée, et videra le livre familial de son intérêt le
   * jour du lancement : **c'est le minimum vital, pas du confort.**
   *
   * `null` = mode normal. Un `Set` vide = mode sélection, rien de coché — les
   * deux états sont distincts, et confondre « pas de sélection » avec « pas en
   * train de trier » ferait disparaître la barre au premier décochage.
   */
  const [selection, setSelection] = useState<Set<string> | null>(null);
  const [triEnCours, setTriEnCours] = useState(false);
  const [triErreur, setTriErreur] = useState<string | null>(null);
  /** Incrémenté après une bascule en lot : la liste doit refléter le nouvel état. */
  const [reloadToken, setReloadToken] = useState(0);
  const session = useSession();
  const modePartage = session?.device != null;
  /**
   * L'écran de la bibliothèque — ni les archives, ni le fonds (US-97). C'est
   * lui qui porte les gestes de *sa* bibliothèque : composer le livre de
   * famille, ouvrir le sas, ajouter une recette. Aucun n'a de sens sur un
   * fonds qu'on ne modifie pas et qu'on n'a pas capturé.
   */
  const bibliotheque = !archived && perimetre === 'livre';
  const surLeFonds = perimetre === 'fonds';
  /**
   * Le bouton flottant n'existe que sur la bibliothèque, hors mode cuisine
   * partagée (US-90, même condition que celle d'`App.tsx` qui le monte) :
   * `route.name === 'library' && !modePartage`. La réserve de fin de liste
   * suit cette présence plutôt que d'être posée à l'aveugle, sans quoi les
   * archives, le fonds ou une tablette de cuisine hériteraient d'un vide sans
   * bouton au-dessus.
   */
  const reserveFAB = bibliotheque && !modePartage;
  const gridClasses = `${GRID_BASE_CLASSES} ${reserveFAB ? FAB_RESERVE_CLASSES : ''}`.trim();
  /**
   * Le sas (US-71), et son lien (US-72). Le même hook que la pastille de la
   * navigation : deux lectures recopiées divergeraient le jour où l'une gagne
   * un cas, et la pastille dirait trois quand le lien dirait deux.
   */
  const sasEnAttente = useSasEnAttente(modePartage);
  /**
   * La même règle que le serveur, importée et non recopiée
   * (`shared/src/access.ts`). Tant que la session n'est pas connue, on
   * considère que rien n'est modifiable : mieux vaut un contrôle qui apparaît
   * qu'un contrôle qui disparaît sous le doigt.
   */
  const estModifiable = useCallback(
    (recipe: RecipeListItem): boolean =>
      session?.member != null && canEdit(session.member, recipe),
    [session],
  );
  const [error, setError] = useState<string | null>(null);
  // Instant de référence figé au montage plutôt que `Date.now()` appelé
  // pendant le rendu de chaque carte : un rendu doit être pur, et « il y a
  // 3 semaines » n'a de toute façon pas besoin d'être recalculé à la seconde.
  const [now] = useState(() => Date.now());
  // Recherche et filtre dans l'URL depuis US-13, plus dans un état local : le
  // retour arrière restitue la liste telle qu'elle était, et une recherche est
  // partageable. `''` vaut « aucun filtre » dans les deux cas.
  const url = useUrl();
  const query = useSearchParam('q');
  const sort = useSearchParam('tri');
  // Statut et étiquettes (US-21) : filtrés **côté serveur** depuis cette
  // story, là où le filtre par étiquette était appliqué après coup sur la
  // page déjà chargée. Filtrer localement revenait à filtrer un sous-ensemble
  // arbitraire dès que la bibliothèque dépassait une page.
  const status = useSearchParam('statut');
  // Nature du plat et cuisine (US-55) : dans l'URL comme le reste des filtres,
  // donc partageables et restituées par le bouton retour.
  const course = useSearchParam('type');
  const cuisine = useSearchParam('cuisine');
  // Mode « avec ce que j'ai » (US-29) : une adresse comme le reste, donc
  // partageable et restituée par le bouton retour.
  const pantryParam = useSearchParam('avec');
  const pantry = useMemo(
    () => pantryParam.split(',').map((name) => name.trim()).filter((name) => name.length > 0),
    [pantryParam],
  );
  const tagsParam = useSearchParam('etiquettes');
  const tags = useMemo(
    () => tagsParam.split(',').map((name) => name.trim()).filter((name) => name.length > 0),
    [tagsParam],
  );

  useEffect(() => {
    const controller = new AbortController();
    // Deux questions différentes, deux requêtes : « où est cette recette ? »
    // (recherche plein texte) et « qu'est-ce que je peux faire avec ce que
    // j'ai ? ». La seconde classe par manquants, la première par pertinence.
    if (pantry.length > 0) {
      searchByIngredients(pantry, controller.signal, perimetre)
        .then((data) => { setRecipes(data); setError(null); })
        .catch((e: unknown) => {
          if (e instanceof Error && e.name !== 'AbortError') setError(e.message);
        });
      return () => { controller.abort(); };
    }
    listRecipes(query, {
      signal: controller.signal,
      archived,
      sort: sort === '' ? 'recent' : sort,
      status,
      tags,
      course,
      cuisine,
      perimetre,
    })
      .then((data) => {
        setRecipes(data);
        setError(null);
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setError(e.message);
      });
    return () => { controller.abort(); };
  }, [refreshKey, reloadToken, query, archived, sort, status, tags, course, cuisine, pantry, perimetre]);

  /**
   * **Ce que la recherche a trouvé de l'autre côté** (US-97).
   *
   * Chercher « blanquette » depuis la bibliothèque et ne rien trouver alors que
   * la recette existe dans le fonds serait pire que le mélange qu'on vient de
   * défaire. La recherche cherche donc dans les deux, et **groupe** : le livre
   * d'abord, puis une ligne qui dit combien le fonds en a et y mène.
   *
   * Une seconde requête plutôt qu'un `perimetre=tout` découpé à l'arrivée :
   * découper côté client ferait transiter deux cents recettes pour en compter
   * trois, et surtout rendrait l'écran responsable d'une règle qui vit au
   * serveur. Elle ne part que depuis la bibliothèque et que sur une recherche
   * — c'est le seul moment où la question se pose.
   */
  /**
   * Le décompte **porte la question à laquelle il répond**, et le rendu vérifie
   * qu'elle est toujours celle qu'on pose. Sans ce couple, l'effet devrait
   * remettre l'état à zéro à chaque frappe — un `setState` synchrone dans un
   * effet, que le lint refuse à juste titre — et, pendant le vol de la requête
   * suivante, l'écran afficherait le compte de la recherche précédente sous la
   * recherche en cours.
   */
  const [dansLeFonds, setDansLeFonds] = useState<{ q: string; n: number } | null>(null);
  const chercheDansLeFonds = bibliotheque && query !== '' && pantry.length === 0;
  useEffect(() => {
    if (!chercheDansLeFonds) return undefined;
    const controller = new AbortController();
    listRecipes(query, { signal: controller.signal, perimetre: 'fonds', sort, status, tags, course, cuisine })
      .then((data) => { setDansLeFonds({ q: query, n: data.length }); })
      // Un échec ici ne mérite pas un bandeau : la réponse principale est
      // arrivée, et « le fonds en a peut-être » n'est pas une information dont
      // l'absence casse quoi que ce soit.
      .catch(() => { /* le groupe ne s'affiche pas, et c'est tout */ });
    return () => { controller.abort(); };
  }, [chercheDansLeFonds, query, sort, status, tags, course, cuisine, refreshKey]);
  /** Le nombre à afficher, ou `null` — hors recherche, ou réponse à une autre question. */
  const compteDuFonds = chercheDansLeFonds && dansLeFonds?.q === query ? dansLeFonds.n : null;

  const visibleRecipes = recipes;
  /** Celles que le tri en lot peut réellement toucher (US-49, correctif). */
  const modifiables = (visibleRecipes ?? []).filter(estModifiable);

  const hasFilters = query !== '' || status !== '' || tags.length > 0 || pantry.length > 0;
  /** Libellé du filtre de statut, pour le compteur de résultats. */
  const statusLabel = status === 'a_cuisiner' ? 'à cuisiner' : status === 'favorite' ? 'favorites' : '';

  function clearFilters(): void {
    navigate(withSearchParams(url, { q: '', statut: '', etiquettes: '', avec: '' }), { replace: true });
  }

  return (
    <section aria-labelledby="bibliotheque-titre" className="flex flex-col gap-6">
      {/*
        **L'en-tête de la bibliothèque** (refonte éditoriale).

        Ce qu'il remplace : sept blocs empilés, chacun sur sa propre rangée
        pleine largeur — titre, puis recherche et tri, puis « Filtrer », puis
        « Chercher avec ce que j'ai », puis « Voir les archives » et « Mes
        collections », puis le compteur, puis « Composer le livre de famille »
        aligné à droite tout seul. Sept rangées, six intentions différentes,
        aucune hiérarchie entre elles, et près de 500 px de hauteur avant la
        première photo sur un écran de bureau. C'est le « fouillis » que
        l'audit externe nommait sans pouvoir le rattacher à une règle : il n'y
        avait pas de motif d'en-tête à respecter.

        Ce qu'il devient, en trois rangs et rien d'autre :

        1. le titre, ce qu'il désigne, et les **destinations voisines** —
           archives, collections, composition du livre — qui sont des liens
           vers ailleurs, pas des réglages de cette liste ;
        2. la **barre d'outils** : chercher, trier, filtrer. Tout ce qui agit
           sur la liste affichée, et rien d'autre, groupé dans un creux qui
           dit « ces contrôles vont ensemble » ;
        3. le compteur, qui est la **réponse** de la barre d'outils et se
           place donc juste sous elle.

        Le titre devient un `h1`. C'était un `h2` — donc la bibliothèque, seul
        écran de l'application, n'avait aucun titre de premier niveau, alors
        que le Journal, la Semaine, les Courses et les Réglages en ont un. Un
        document qui commence en `h2` se parcourt mal en navigation par
        titres, et c'était précisément le défaut qu'US-56 entendait corriger.
      */}
      <PageHeader
        // La bibliothèque est le seul écran dont l'onglet est actif **et**
        // porte le même mot que son titre. Les archives et le fonds gardent
        // le leur : rien d'autre à l'écran ne dit où l'on est.
        titrePorteParLaNavigation={bibliotheque}
        title={
          <span id="bibliotheque-titre">
            {archived ? 'Recettes archivées' : surLeFonds ? NOM_DU_FONDS : 'Mes recettes'}
          </span>
        }
        lede={
          archived
            ? 'Rien n’est jamais supprimé. Ce qui est ici peut revenir dans la bibliothèque à tout moment.'
            // Dire ce qu'est cet écran, et surtout ce qu'on y fait : ces
            // recettes ne sont pas les vôtres tant que vous ne les avez pas
            // copiées, et aucune n'a été cuisinée ici — l'étiquette « jamais
            // cuisinée » de chaque carte dit vrai, elle ne signale pas un
            // oubli.
            : surLeFonds
              ? 'Des recettes écrites pour Mijoté, à faire au Thermomix. Aucune n’a encore été cuisinée ici : ouvrez-en une, copiez-la, et elle rejoindra vos recettes.'
              : undefined
        }
        actions={
          <>
            {/* **Le sas** (US-71), et c'est US-72 qui a rendu ce lien
                nécessaire — puis évident.

                La pastille de la navigation annonce ce qui attend « à la
                porte », mais l'onglet « Recettes » mène à la bibliothèque, et
                la bibliothèque ne proposait pas le sas : il ne s'ouvrait qu'en
                tapant `/sas` dans la barre d'adresse. Un compteur qui pointe
                vers un endroit inatteignable est le contraire de ce que doit
                faire une pastille. Le défaut vient d'US-71 ; il ne pouvait pas
                se voir avant US-72, puisque rien ne remplissait le sas.

                **Seulement quand il y a quelque chose**, et le décompte avec :
                « un groupe vide ne s'affiche pas, et aucune puce ne porte le
                compteur zéro » (§4). Un contrôle permanent de plus sur un écran
                dont la densité est mesurée par un test depuis US-56 se serait
                payé tous les jours pour un usage occasionnel. */}
            {bibliotheque && !modePartage && sasEnAttente > 0 && (
              <Link to={paths.importInbox} className={buttonClasses('quiet', 'compact')}>
                Le sas ({sasEnAttente})
              </Link>
            )}
            {/* Les archives **changent d'écran** : ce lien était aligné avec
                la recherche et le tri, comme s'il affinait la liste (US-56).
                Il est ici au rang des destinations, avec les collections.

                Depuis le fonds (US-97), le retour se fait aussi par ici : deux
                écrans qui ne sont pas la bibliothèque, un même chemin pour y
                revenir. */}
            <Link
              to={archived || surLeFonds ? paths.library : paths.archives}
              className={buttonClasses('quiet', 'compact')}
            >
              {archived || surLeFonds
                ? <><Icon name="fleche-gauche" size={0.9} /> Revenir à la bibliothèque</>
                : 'Recettes archivées'}
            </Link>
            {/* L'écran des collections (US-63). Il s'atteint d'ici plutôt que
                depuis une sixième entrée de navigation : une collection est
                une façon de lire sa bibliothèque, et la densité de la barre
                est mesurée par un test depuis US-56. */}
            {bibliotheque && (
              <Link to={paths.collections} className={buttonClasses('quiet', 'compact')}>
                Mes collections
              </Link>
            )}
            {/* **Le fonds Thermomix** (US-97), au même rang que les archives et
                les collections — et pour la même raison : c'est une **autre
                façon de lire des recettes**, pas une sixième entrée de
                navigation. La barre en compte cinq depuis US-56, et un test le
                mesure (`expect(ENTREES).toHaveLength(5)`).

                Sans compteur : « 200 » sur un lien permanent ferait du fonds
                le chiffre le plus gros de l'écran, exactement ce que cette
                story vient de retirer de l'accueil. */}
            {bibliotheque && (
              <Link to={paths.fonds} className={buttonClasses('quiet', 'compact')}>
                {NOM_DU_FONDS}
              </Link>
            )}
            {/* Le tri en lot est une écriture : rien à faire sur un appareil
                en mode partagé (US-50). Il rejoint les autres destinations —
                c'est un mode, comme les archives, pas un filtre. */}
            {/* Seulement quand la bibliothèque a quelque chose à composer :
                proposer de composer un livre de famille vide n'a pas de sens,
                et la densité de l'accueil avant la première recette est
                mesurée par un test depuis US-56. */}
            {bibliotheque && !modePartage && selection === null && (visibleRecipes?.length ?? 0) > 0 && (
              <Button type="button" variant="quiet" size="compact" onClick={() => { setSelection(new Set()); setTriErreur(null); }}>
                Composer le livre de famille
              </Button>
            )}
          </>
        }
      >
        {/*
          La barre d'outils. `bg-surface-sunken` : un creux qui rassemble les
          trois contrôles agissant sur la liste. Sans lui, un champ, une liste
          déroulante et un bouton posés côte à côte sur le fond de page se
          lisent comme trois éléments sans rapport — ce qu'ils étaient.
        */}
        <div className="relative flex flex-row items-center gap-2 rounded-card bg-surface-sunken p-2 xs:gap-3 xs:p-3">
          <div className="min-w-0 flex-1">
            <label htmlFor="recipe-search" className="sr-only">
              Rechercher une recette
            </label>
            <input
              id="recipe-search"
              type="search"
              value={query}
              // `replace` : sans lui, chaque caractère frappé empilerait une
              // entrée d'historique et rendrait le bouton retour inutilisable.
              onChange={(e) => { navigate(withSearchParam(url, 'q', e.target.value), { replace: true }); }}
              // **Un placeholder qui tient dans le champ** (US-105). « Rechercher
              // un titre, un ingrédient, une étape… » se coupait au milieu du
              // mot sur un téléphone : un texte tronqué n'informe pas, il
              // signale un défaut. La version longue reste au-dessus de 480 px,
              // où elle tient.
              placeholder={archived ? 'Rechercher…' : 'Rechercher une recette…'}
              // `bg-surface` sur un creux : le champ **remonte** au-dessus de
              // la barre qui le contient. C'est la relation inverse de
              // l'ancienne, où un champ blanc bordé flottait sur un fond
              // blanc et n'avait que sa bordure pour exister.
              className="min-h-touch w-full rounded-control border border-border-interactive bg-surface px-3 text-base text-foreground placeholder:text-foreground-muted"
            />
          </div>
          {/* Barre de filtres groupée et libellée (US-21). Absente de l'écran
              des archives : on n'y range pas, on y restaure. Elle est
              **dans** la barre d'outils depuis la refonte éditoriale : le
              bouton « Filtrer » vivait sur sa propre rangée, sous la
              recherche, sans rien pour dire qu'ils agissent sur la même
              liste. */}
          {!archived && (
            <LibraryFilters
              query={query}
              status={status}
              sort={sort}
              tags={tags}
              course={course}
              cuisine={cuisine}
              url={url}
              refreshKey={refreshKey}
            />
          )}
        </div>

        {/* « Qu'est-ce que je peux faire avec ce que j'ai » (US-29) : une autre
            question que « où est cette recette », et donc un autre mode. Sous
            la barre d'outils et non dedans, pour cette raison exacte. */}
        {!archived && <PantrySearch pantry={pantry} url={url} />}
      </PageHeader>

      {/* Compteur de résultats (US-20) : l'utilisateur ne savait jamais combien
          de recettes répondaient à sa recherche ni si un filtre était actif.
          `aria-live` sans `role="status"` : le nombre mérite d'être annoncé
          quand il change, mais un second rôle « status » dans la page entrerait
          en concurrence avec les bandeaux de confirmation. */}
      {error === null && visibleRecipes !== null && visibleRecipes.length > 0 && (
        <p data-testid="result-count" aria-live="polite" className="text-meta">
          <span className="text-data">{visibleRecipes.length}</span>
          {visibleRecipes.length > 1 ? ' recettes' : ' recette'}
          {query !== '' && ` pour « ${query} »`}
          {statusLabel !== '' && ` · ${statusLabel}`}
          {tags.length > 0 && ` · ${tags.map((name) => `« ${name} »`).join(' et ')}`}
          {hasFilters && (
            <>
              {' · '}
              <button
                type="button"
                onClick={clearFilters}
                className={`rounded-control underline ${INTERACTIVE}`}
              >
                Tout effacer
              </button>
            </>
          )}
        </p>
      )}

      {error !== null && <Banner tone="danger">Impossible de charger les recettes : {error}</Banner>}

      {error === null && recipes === null && (
        <div className={gridClasses} aria-hidden="true">
          {Array.from({ length: 8 }, (_, index) => (
            <RecipeCardSkeleton key={index} />
          ))}
        </div>
      )}

      {/* Quatre états distincts (US-20, étendus en US-21), là où un seul
          message servait pour tout : archives vides, recherche sans résultat,
          filtres sans résultat, bibliothèque vide. Chacun dit ce qui se passe
          et comment en sortir — l'ordre des cas va du plus précis au plus
          général. */}
      {error === null && recipes !== null && recipes.length === 0 && (
        archived ? (
          <EmptyState
            icon={<LogoMark size={52} className="opacity-90" />}
            title="Aucune recette archivée."
            description="Les recettes que vous archivez se retrouvent ici."
            action={<Link to={paths.library} className={buttonClasses('secondary')}><Icon name="fleche-gauche" size={0.9} /> Revenir à la bibliothèque</Link>}
          />
        ) : query !== '' ? (
          <EmptyState
            icon={<PhotoPlaceholder />}
            title={`Aucune recette ne correspond à « ${query} ».`}
            description={
              status !== '' || tags.length > 0
                ? 'Des filtres sont également actifs : ce sont peut-être eux qui excluent le résultat.'
                : 'La recherche porte sur le titre, les ingrédients et les étapes.'
            }
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button type="button" variant="secondary" onClick={clearFilters}>
                  {status !== '' || tags.length > 0 ? 'Tout effacer' : 'Effacer la recherche'}
                </Button>
                <Link
                  to={withSearchParam(paths.archives, 'q', query)}
                  className={buttonClasses('ghost', 'compact')}
                >
                  Chercher dans les archives
                </Link>
              </div>
            }
          />
        ) : hasFilters ? (
          <EmptyState
            icon={<PhotoPlaceholder />}
            title="Aucune recette ne correspond à ces filtres."
            // Les critères sont énumérés : sans cela, l'utilisateur qui a
            // coché trois puces ne sait pas laquelle exclut tout.
            description={[statusLabel, ...tags].filter((part) => part !== '').join(' · ')}
            action={
              <Button type="button" variant="secondary" onClick={clearFilters}>
                Tout effacer
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<LogoMark size={52} className="opacity-90" />}
            title="Aucune recette pour l’instant."
            description="Ajoutez-en une à la main, ou importez-la depuis une URL ou un texte collé."
            action={
              <Link to={paths.newRecipe} className={buttonClasses('primary')}>
                Ajouter une recette
              </Link>
            }
          />
        )
      )}

      {error === null && visibleRecipes !== null && visibleRecipes.length > 0 && (
        <>
          {/* Le tri en lot est une écriture : il n'a rien à faire sur un
              appareil en mode partagé (US-50). */}
          {!modePartage && <BarreDeTri
            selection={selection}
            verrouillees={visibleRecipes.length - modifiables.length}
            total={modifiables.length}
            enCours={triEnCours}
            erreur={triErreur}
            onFermer={() => { setSelection(null); setTriErreur(null); }}
            onToutCocher={() => { setSelection(new Set(modifiables.map((r) => r.id))); }}
            onAppliquer={(visibility) => {
              if (selection === null || selection.size === 0) return;
              setTriEnCours(true);
              setTriErreur(null);
              setRecipesVisibility([...selection], visibility)
                .then(() => { setSelection(null); setReloadToken((n) => n + 1); })
                .catch((e: unknown) => { setTriErreur(e instanceof Error ? e.message : String(e)); })
                .finally(() => { setTriEnCours(false); });
            }}
          />}
          <ul data-testid="recipe-list" className={gridClasses}>
            {visibleRecipes.map((recipe) => (
              <li key={recipe.id} className="flex">
                <RecipeCard
                  recipe={recipe}
                  now={now}
                  missing={'missing' in recipe ? (recipe as RecipeByIngredients).missing : null}
                  couverture={
                    'matched_count' in recipe && pantry.length > 1
                      ? { utilises: (recipe as RecipeByIngredients).matched_count, demandes: pantry.length }
                      : null
                  }
                  modifiable={estModifiable(recipe)}
                  supprimable={archived && estModifiable(recipe) && session?.member?.role === 'proprietaire'}
                  selectionnee={selection === null ? null : selection.has(recipe.id)}
                  onSelection={(cochee) => {
                    setSelection((courante) => {
                      const suivante = new Set(courante ?? []);
                      if (cochee) suivante.add(recipe.id); else suivante.delete(recipe.id);
                      return suivante;
                    });
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {/* **Le second groupe : ce que le fonds a trouvé** (US-97).
          Après le livre, jamais mêlé à lui — c'est tout l'objet de la story —
          et jamais à zéro : « un groupe vide ne s'affiche pas, et aucune puce
          ne porte le compteur zéro » (§4). Il vaut aussi, et surtout, quand la
          bibliothèque n'a rien ramené : chercher « blanquette » et se voir
          répondre « rien » alors que la recette existe serait pire que le
          mélange qu'on vient de défaire.

          Le terme de recherche voyage avec le lien : on arrive sur le fonds
          déjà cherché, pas sur ses deux cents lignes à refaire le geste. */}
      {compteDuFonds !== null && compteDuFonds > 0 && (
        <p data-testid="groupe-du-fonds" className="text-meta">
          <Link
            to={withSearchParam(paths.fonds, 'q', query)}
            className={`inline-flex min-h-touch-min items-center rounded-control underline ${INTERACTIVE}`}
          >
            Dans le fonds Thermomix : {compteDuFonds}
            {compteDuFonds > 1 ? ' recettes' : ' recette'}
          </Link>
        </p>
      )}
    </section>
  );
}
